import type { ReactNode } from 'react'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { Pressable } from './Pressable'

export interface LedgerRowProps {
  /** Leading slot: status mark (e.g. StatusMark), time (e.g. "14:32"), or icon */
  leading?: ReactNode
  /** Primary title */
  title: ReactNode
  /** Secondary meta + status line */
  subtitle?: ReactNode
  /** Trailing numeric figure rendered in monospace tabular numbers */
  trailingFigure?: ReactNode
  /** Show trailing chevron for navigation */
  trailingChevron?: boolean
  /** Optional badge or chip in trailing slot */
  trailingBadge?: ReactNode
  /** When provided, renders as an interactive button with focus ring */
  onClick?: () => void
  disabled?: boolean
  ariaLabel?: string
  className?: string
  testId?: string
}

/**
 * LedgerRow — dense row primitive for ledger lists (Stock movements, Approvals, Today queue).
 * Supports leading mark/time, title, meta/status, trailing mono figure or chevron, and pressable button variant.
 */
export function LedgerRow({
  leading,
  title,
  subtitle,
  trailingFigure,
  trailingChevron = false,
  trailingBadge,
  onClick,
  disabled = false,
  ariaLabel,
  className,
  testId,
}: LedgerRowProps) {
  const isInteractive = typeof onClick === 'function'

  const content = (
    <>
      {leading ? (
        <span className="flex-none flex items-center justify-center min-w-[20px] text-xs text-[var(--muted)]" aria-hidden="true">
          {leading}
        </span>
      ) : null}

      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <span className="text-xs font-semibold text-[var(--text-strong)] truncate leading-snug">
          {title}
        </span>
        {subtitle ? (
          <span className="text-[11px] text-[var(--muted)] truncate leading-tight mt-0.5">
            {subtitle}
          </span>
        ) : null}
      </div>

      {(trailingFigure !== undefined && trailingFigure !== null) || trailingBadge || trailingChevron ? (
        <div className="flex-none flex items-center gap-2 text-right">
          {trailingBadge ? (
            <span className="flex-none">{trailingBadge}</span>
          ) : null}
          {trailingFigure !== undefined && trailingFigure !== null ? (
            <span
              className="text-xs font-semibold text-[var(--text-strong)]"
              style={{ fontFamily: 'var(--font-figures)', fontVariantNumeric: 'tabular-nums' }}
            >
              {trailingFigure}
            </span>
          ) : null}
          {trailingChevron ? (
            <span className="flex-none text-[var(--muted)] flex items-center justify-center -mr-1" aria-hidden="true">
              <KeyboardArrowRightRoundedIcon fontSize="small" />
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  )

  const commonClass = `w-full flex items-center gap-3 px-3.5 py-2.5 border-b border-[var(--border)] transition-colors select-none ${
    disabled ? 'opacity-40 pointer-events-none' : ''
  } ${className ?? ''}`

  if (isInteractive) {
    return (
      <Pressable
        role="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`${commonClass} text-left bg-[var(--surface)] hover:bg-[var(--surface-raised)] active:bg-[var(--surface-raised)] cursor-pointer focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-[-2px]`}
      >
        {content}
      </Pressable>
    )
  }

  return (
    <div
      data-testid={testId}
      aria-label={ariaLabel}
      className={`${commonClass} bg-[var(--surface)]`}
    >
      {content}
    </div>
  )
}
